import * as React from "react";
import { CalendarDays, Loader2, Search, SearchX, Shield, UserCheck, UserPlus, UserRoundSearch } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { lookupUserByUsername, type UserProfile } from "@/data/users-api";
import { addBrandMember, fetchBrand, toAdminMember, type AdminMember } from "@/data/brands-api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConfirmAddDialog } from "@/features/members/confirm-add-dialog";

/** The username lookup's lifecycle (step 1 of the add flow). */
type Lookup =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "notFound"; username: string }
  | { state: "alreadyMember"; profile: UserProfile }
  | { state: "found"; profile: UserProfile };

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

function formatJoined(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

/** Round profile picture, falling back to initials on the brand gradient. */
function ProfileAvatar({
  name,
  photoURL,
  className,
}: {
  name: string;
  photoURL: string;
  className: string;
}) {
  const [broken, setBroken] = React.useState(false);
  if (photoURL && !broken) {
    return (
      <img
        src={photoURL}
        alt={name}
        onError={() => setBroken(true)}
        className={cn("shrink-0 rounded-full border border-border object-cover", className)}
      />
    );
  }
  return (
    <div
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-brand-gradient font-bold text-white",
        className,
      )}
    >
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

/** Step 1's result: the profile preview the owner verifies before confirming. */
function ProfilePreview({
  profile,
  brandName,
  onReject,
  onAccept,
}: {
  profile: UserProfile;
  brandName: string;
  onReject: () => void;
  onAccept: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <UserRoundSearch /> Is this the right person?
        </CardTitle>
        <span className="ml-auto rounded-full border border-border-strong bg-panel-2 px-2.5 py-1 text-[10px] font-extrabold tracking-[.07em] text-text-3">
          PROFILE PREVIEW
        </span>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex gap-4">
          <ProfileAvatar name={profile.displayName} photoURL={profile.photoURL} className="size-[72px] text-[22px]" />
          <div className="min-w-0 flex-1">
            <div className="text-[17px] font-extrabold tracking-[-.01em]">{profile.displayName}</div>
            <div className="font-mono text-[12px] text-brand">@{profile.username}</div>
            <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-text-3">
              <CalendarDays className="size-[13px]" />
              On pop.in since {formatJoined(profile.createdAt)}
            </div>
          </div>
        </div>

        {profile.bio && (
          <div className="mt-3 rounded-[10px] border border-border border-l-[3px] border-l-brand bg-panel-2 px-3 py-2.5 text-[13px] text-muted-foreground">
            &ldquo;{profile.bio}&rdquo;
          </div>
        )}

        <div className="mt-4 flex items-center gap-2.5 border-t border-dashed border-border-strong pt-4">
          <div className="flex-1 text-[12.5px] font-semibold text-muted-foreground">
            Double-check the username and picture — you&rsquo;re granting access to your brand.
          </div>
          <Button variant="ghost" size="sm" onClick={onReject}>
            Not them
          </Button>
          <Button size="sm" onClick={onAccept}>
            <UserPlus className="size-4" /> Add to {brandName}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Shimmering placeholder while the profile lookup is in flight. */
function ProfileSkeleton() {
  return (
    <Card>
      <CardContent>
        <div className="flex animate-pulse gap-4">
          <div className="size-[72px] shrink-0 rounded-full bg-panel-2" />
          <div className="flex-1">
            <div className="mt-1.5 h-[18px] w-[170px] rounded-lg bg-panel-2" />
            <div className="mt-2 h-3 w-[90px] rounded-lg bg-panel-2" />
            <div className="mt-2.5 h-[11px] w-[130px] rounded-lg bg-panel-2" />
          </div>
        </div>
        <div className="mt-3.5 h-[42px] animate-pulse rounded-[10px] bg-panel-2" />
      </CardContent>
    </Card>
  );
}

export function MembersPage() {
  const { activeBrand, isSuperAdmin, user } = usePortal();
  const brandId = activeBrand?.id ?? "";
  const brandName = activeBrand?.name ?? "";
  // The add flow is owner-only (the backend enforces this; super admins may
  // manage any brand). Plain members still get the read-only roster.
  const canManage = activeBrand?.viewerRole === "OWNER" || isSuperAdmin;

  // Null until the roster for the active brand has loaded.
  const [members, setMembers] = React.useState<AdminMember[] | null>(null);
  const [lookup, setLookup] = React.useState<Lookup>({ state: "idle" });
  const [username, setUsername] = React.useState("");
  // The profile in the step-2 confirm dialog; null = closed.
  const [confirming, setConfirming] = React.useState<UserProfile | null>(null);
  const [adding, setAdding] = React.useState(false);

  // (Re)load the roster — and reset any in-progress add — per brand. `myBrands`
  // already ships members, but a fresh fetch keeps the roster current when
  // co-owners have made changes since sign-in.
  React.useEffect(() => {
    setMembers(null);
    setLookup({ state: "idle" });
    setUsername("");
    setConfirming(null);
    if (!brandId) return;
    let cancelled = false;
    fetchBrand(brandId)
      .then((brand) => {
        if (!cancelled) setMembers(brand ? brand.members.map(toAdminMember) : []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setMembers([]);
        toast.error(errorMessage(e, "Couldn’t load this brand’s members."));
      });
    return () => {
      cancelled = true;
    };
  }, [brandId]);

  async function runLookup() {
    const uname = username.trim().replace(/^@/, "");
    if (!uname || lookup.state === "loading") return;
    setLookup({ state: "loading" });
    try {
      const profile = await lookupUserByUsername(uname);
      if (!profile) {
        setLookup({ state: "notFound", username: uname });
      } else if (members?.some((m) => m.username === profile.username)) {
        setLookup({ state: "alreadyMember", profile });
      } else {
        setLookup({ state: "found", profile });
      }
    } catch (e) {
      setLookup({ state: "idle" });
      toast.error(errorMessage(e, "Couldn’t look up that username."));
    }
  }

  async function confirmAdd() {
    if (!confirming || adding) return;
    setAdding(true);
    try {
      const member = await addBrandMember({
        brandId,
        username: confirming.username,
        role: "MEMBER",
      });
      setMembers((ms) => [...(ms ?? []), toAdminMember(member)]);
      setConfirming(null);
      setLookup({ state: "idle" });
      setUsername("");
      toast.success(`${confirming.displayName} was added to ${brandName}`);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn’t add that member."));
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div>
        <h1 className="text-2xl font-extrabold tracking-[-.02em]">Members</h1>
        <p className="mb-[26px] mt-1.5 max-w-[560px] text-sm text-muted-foreground">
          {canManage ? (
            <>
              Give people on pop.in access to manage {brandName}. Look someone up by their
              username, make sure it&rsquo;s really them, then confirm what they&rsquo;ll be able
              to do.
            </>
          ) : (
            <>Everyone with access to {brandName}. Only brand owners can add members.</>
          )}
        </p>

        {canManage && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>
                  <UserPlus /> Add a member
                </CardTitle>
                <span className="ml-auto rounded-full border border-border-strong bg-panel-2 px-2.5 py-1 text-[10px] font-extrabold tracking-[.07em] text-text-3">
                  STEP 1 · FIND THEM
                </span>
              </CardHeader>
              <CardContent className="pt-4">
                <Label htmlFor="member-username" className="mb-[7px] block">
                  pop.in username
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[13px] font-bold text-text-3">
                      @
                    </span>
                    <Input
                      id="member-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder="username"
                      className="pl-7"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void runLookup();
                      }}
                    />
                  </div>
                  <Button
                    size="sm"
                    className="shrink-0"
                    disabled={!username.trim() || lookup.state === "loading" || members === null}
                    onClick={() => void runLookup()}
                  >
                    {lookup.state === "loading" ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Search className="size-4" />
                    )}
                    {lookup.state === "loading" ? "Looking…" : "Look up"}
                  </Button>
                </div>
                <p className="mt-2 text-[11.5px] text-text-3">
                  Usernames are exact — ask them for theirs if you&rsquo;re not sure.
                </p>
              </CardContent>
            </Card>

            <div className="mt-4">
              {lookup.state === "loading" && <ProfileSkeleton />}

              {lookup.state === "notFound" && (
                <div className="flex items-center gap-3.5 rounded-2xl border border-dashed border-border-strong px-5 py-[18px] text-text-3">
                  <div className="grid size-[38px] shrink-0 place-items-center rounded-xl bg-panel-2">
                    <SearchX className="size-[18px]" />
                  </div>
                  <div>
                    <div className="text-[13.5px] font-bold text-muted-foreground">
                      No one&rsquo;s called @{lookup.username} on pop.in
                    </div>
                    <div className="text-[12px]">
                      Check the spelling — usernames are exact, with no spaces.
                    </div>
                  </div>
                </div>
              )}

              {lookup.state === "alreadyMember" && (
                <div className="flex items-center gap-3.5 rounded-2xl border border-[color-mix(in_oklab,#f5b945_45%,var(--border))] bg-[color-mix(in_oklab,#f5b945_9%,var(--card))] px-5 py-[18px]">
                  <div className="grid size-[38px] shrink-0 place-items-center rounded-xl bg-[color-mix(in_oklab,#f5b945_22%,transparent)] text-[#f5b945]">
                    <UserCheck className="size-[18px]" />
                  </div>
                  <div>
                    <div className="text-[13.5px] font-bold">
                      {lookup.profile.displayName} is already a member
                    </div>
                    <div className="text-[12px] text-muted-foreground">
                      @{lookup.profile.username} already has access to {brandName}.
                    </div>
                  </div>
                </div>
              )}

              {lookup.state === "found" && (
                <ProfilePreview
                  profile={lookup.profile}
                  brandName={brandName}
                  onReject={() => {
                    setLookup({ state: "idle" });
                    setUsername("");
                  }}
                  onAccept={() => setConfirming(lookup.profile)}
                />
              )}
            </div>
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <Shield /> Current members
          </CardTitle>
          {members !== null && (
            <span className="ml-auto rounded-full border border-border-strong bg-panel-2 px-2.5 py-0.5 text-[10.5px] font-extrabold text-text-3">
              {members.length}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-2 pt-4">
          {members === null ? (
            <div className="flex items-center justify-center gap-2 py-8 text-[13px] text-text-3">
              <Loader2 className="size-4 animate-spin" /> Loading members…
            </div>
          ) : members.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border-strong py-8 text-center text-[13px] text-text-3">
              No members yet.
            </div>
          ) : (
            members.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 rounded-xl border border-border bg-panel-2 px-3 py-2.5"
              >
                <ProfileAvatar name={m.name} photoURL={m.photoURL} className="size-9 text-[12px]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold">
                    {m.name}
                    {m.username === user.username && (
                      <span className="text-text-3"> (you)</span>
                    )}
                  </div>
                  {m.username && (
                    <div className="truncate font-mono text-[10.5px] text-text-3">@{m.username}</div>
                  )}
                </div>
                <span
                  className={cn(
                    "shrink-0 rounded-[7px] border px-2 py-[3px] text-[9.5px] font-extrabold tracking-[.07em]",
                    m.role === "OWNER"
                      ? "border-[color-mix(in_oklab,var(--brand)_40%,var(--border))] bg-[color-mix(in_oklab,var(--brand)_10%,transparent)] text-brand"
                      : "border-border-strong bg-card text-text-3",
                  )}
                >
                  {m.role}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <ConfirmAddDialog
        user={confirming}
        brandName={brandName}
        adding={adding}
        onConfirm={() => void confirmAdd()}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      />
    </div>
  );
}
